import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Check, Star, CreditCard, Users, BarChart as ChartBar, Zap, Lock, Sparkles } from 'lucide-react';
import Button from '../../components/ui/Button';

const plans = [
  {
    name: "Free",
    price: "0",
    description: "Perfect for personal finance tracking",
    features: [
      "AI-powered transaction categorization",
      "Basic budgeting tools",
      "Connect up to 2 accounts",
      "Monthly insights report",
      "Email support",
      "Mobile app access",
      "Basic transaction search",
      "Standard data export"
    ],
    icon: <CreditCard className="h-6 w-6" />,
    color: "from-gray-500 to-gray-600",
    isPopular: false,
    badge: null
  },
  {
    name: "Pro",
    price: "9.99",
    description: "Advanced features for power users",
    features: [
      "Everything in Free",
      "Unlimited account connections",
      "Custom budget rules",
      "Real-time alerts",
      "Investment tracking",
      "Priority support",
      "Advanced analytics",
      "API access",
      "Custom categories",
      "Automated reports"
    ],
    icon: <ChartBar className="h-6 w-6" />,
    color: "from-blue-500 to-indigo-600",
    isPopular: true,
    badge: "Most Popular"
  },
  {
    name: "Business",
    price: "29.99",
    description: "Built for small businesses",
    features: [
      "Everything in Pro",
      "Multi-user access",
      "Invoice tracking",
      "Cash flow forecasting",
      "API access",
      "Dedicated account manager",
      "Custom integrations",
      "Team permissions",
      "Audit logs",
      "Premium support"
    ],
    icon: <Users className="h-6 w-6" />,
    color: "from-purple-500 to-purple-600",
    isPopular: false,
    badge: "Best Value"
  }
];

const SubscriptionPlans: React.FC = () => {
  const navigate = useNavigate();
  const [selectedPlan, setSelectedPlan] = useState<string | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);

  const handleSubscribe = async (planName: string) => {
    setSelectedPlan(planName);
    setIsProcessing(true);

    try {
      // Here you would integrate with your payment processor
      // For now, we'll simulate a successful subscription
      await new Promise(resolve => setTimeout(resolve, 1500));
      
      // Navigate to dashboard after successful subscription
      navigate('/dashboard');
    } catch (error) {
      console.error('Subscription error:', error);
      setIsProcessing(false);
    }
  };

  const handleSkip = () => {
    navigate('/dashboard');
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white dark:from-gray-900 dark:to-gray-800">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="text-center mb-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h1 className="text-4xl font-extrabold text-gray-900 dark:text-white mb-4">
              Choose Your Plan
            </h1>
            <p className="text-xl text-gray-600 dark:text-gray-300">
              Select the perfect plan for your financial journey
            </p>
          </motion.div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {plans.map((plan, index) => (
            <motion.div
              key={plan.name}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              className={`relative bg-white dark:bg-gray-800 rounded-2xl shadow-xl ${
                plan.isPopular ? 'ring-2 ring-blue-500 dark:ring-blue-400' : 'ring-1 ring-gray-200 dark:ring-gray-700'
              } transform transition-all duration-300 hover:scale-105 hover:shadow-2xl`}
            >
              {plan.badge && (
                <div className="absolute -top-5 inset-x-0 flex justify-center">
                  <div className={`inline-block px-4 py-1 text-sm font-semibold tracking-wider text-white bg-gradient-to-r ${plan.color} rounded-full shadow-lg transform hover:scale-105 transition-transform duration-200`}>
                    {plan.badge}
                  </div>
                </div>
              )}

              <div className="p-8">
                <div className="flex items-center justify-between mb-6">
                  <div className={`h-12 w-12 rounded-xl bg-gradient-to-r ${plan.color} flex items-center justify-center text-white transform transition-all duration-300 group-hover:scale-110 shadow-lg`}>
                    {plan.icon}
                  </div>
                  {plan.isPopular && (
                    <div className="flex items-center text-blue-500 dark:text-blue-400">
                      <Star className="h-5 w-5 fill-current" />
                      <span className="ml-1 text-sm font-medium">Popular choice</span>
                    </div>
                  )}
                </div>

                <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
                  {plan.name}
                </h3>
                <div className="flex items-baseline text-gray-900 dark:text-white mb-2">
                  <span className="text-4xl font-extrabold tracking-tight">${plan.price}</span>
                  <span className="ml-1 text-xl font-semibold">/month</span>
                </div>
                <p className="text-gray-500 dark:text-gray-400 mb-6">
                  {plan.description}
                </p>

                <ul className="mt-8 space-y-4 mb-8">
                  {plan.features.map((feature, index) => (
                    <li key={index} className="flex items-start">
                      <Check className="h-5 w-5 text-green-500 dark:text-green-400 mt-0.5 flex-shrink-0" />
                      <span className="ml-3 text-gray-600 dark:text-gray-300">
                        {feature}
                      </span>
                    </li>
                  ))}
                </ul>

                <Button
                  fullWidth
                  onClick={() => handleSubscribe(plan.name)}
                  isLoading={isProcessing && selectedPlan === plan.name}
                  className={`${
                    plan.isPopular
                      ? 'bg-gradient-to-r from-blue-500 to-indigo-600 text-white hover:from-blue-600 hover:to-indigo-700'
                      : 'bg-gray-50 text-gray-900 hover:bg-gray-100 dark:bg-gray-700 dark:text-gray-100 dark:hover:bg-gray-600'
                  }`}
                >
                  {plan.price === "0" ? "Get Started" : "Subscribe Now"}
                </Button>
              </div>

              {plan.isPopular && (
                <div className="px-8 pb-8">
                  <div className="border-t border-gray-200 dark:border-gray-700 pt-6 mt-6">
                    <div className="flex items-center">
                      <Lock className="h-5 w-5 text-blue-500 dark:text-blue-400 mr-2" />
                      <span className="text-sm text-gray-500 dark:text-gray-400">
                        30-day money-back guarantee
                      </span>
                    </div>
                    <div className="flex items-center mt-2">
                      <Sparkles className="h-5 w-5 text-blue-500 dark:text-blue-400 mr-2" />
                      <span className="text-sm text-gray-500 dark:text-gray-400">
                        No credit card required for free plan
                      </span>
                    </div>
                  </div>
                </div>
              )}
            </motion.div>
          ))}
        </div>

        <div className="mt-12 text-center">
          <Button
            variant="ghost"
            onClick={handleSkip}
            className="text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200"
          >
            Skip for now
          </Button>
        </div>
      </div>
    </div>
  );
};

export default SubscriptionPlans;